/**
 * BattleScene — the faithful, touch-first turn-based battle UI.
 *
 * The renderer owns NO rules: every outcome comes from the pure engine
 * (core/resolveTurn + core/stats/damage/catch/status/abilities) threaded through a
 * deterministic, forked RNG. The scene's whole job is to (a) let the player choose an
 * action via a big touch menu, (b) animate the engine's ordered BattleEvent[] stream
 * at a calm pace (with a TURBO toggle that still lingers on level-up + evolution
 * flourishes), and (c) at the end, run the post-battle leveling/evolution pipeline and
 * hand the result to the session.
 *
 * Decompression-first (CLAUDE.md pillar 1 + 6):
 *  - Cozy↔Spicy is honored: it shapes the AI's choices (it already shaped the foe's
 *    level upstream in the Route/encounter step).
 *  - NO-STAKES loss: a wipe is "your team is worn out — let's head home", a gentle
 *    fade back to the caller. No money/item loss, no penalty, ever.
 *  - A catch is a quiet joy: the mon joins the team (or a box) and the dex.
 *
 * Launch data is {@link BattleSceneData} — built by the Route/Town scenes. The player
 * side is taken from the live GameSession; the opponent side is supplied by the caller
 * (a wild encounter or a friendly trainer's party).
 */
import Phaser from 'phaser';
import { getSession } from './session.js';
import type { GameSession } from '@/session';
import {
  startBattle,
  resolveTurn,
  makeRng,
  hashString,
  getNature,
  computeStats,
  effectivenessAgainst,
  expGainFromBattle,
  DEFAULT_EXP_SHARE,
  makeLevelUpEvents,
  expBarFraction,
  awardEvs,
  evYieldOf,
  friendshipAfter,
  FRIENDSHIP_ON_WIN,
  FRIENDSHIP_ON_LEVEL_UP,
  checkEvolution,
  evolveInstance,
  DIFFICULTIES,
  foeDamageMultiplier,
  playerDamageMultiplier,
  catchRateMultiplier,
  type BattleDeps,
  type Rng,
} from '@/core';
import type {
  BattleState,
  BattleSide,
  BattleSideId,
  BattleParticipant,
  BattleAction,
  BattleEvent,
  BattleKind,
  PokemonInstance,
  SpeciesData,
  MoveData,
  Difficulty,
  StatusCondition,
  StatKey,
} from '@/shared';
import { STAT_KEYS } from '@/shared';
import type { DataIndex } from '@/data';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET, hexToCss } from './ui/theme.js';
import { Button } from './ui/Button.js';
import { DialogBox } from './ui/DialogBox.js';
import { HPBar } from './ui/HPBar.js';
import { Menu, type MenuItem } from './ui/Menu.js';
import { TextInput } from './ui/TextInput.js';
import { Layout } from './layout/index.js';
import { SpriteResolver } from './sprites/index.js';
import { REGISTRY_DATA_INDEX, REGISTRY_SPRITES, REGISTRY_FX_READY } from './PreloadScene.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import {
  evolutionChoiceLabels,
  isEvolutionCancelKey,
  evolvedMessage,
  stoppedEvolvingMessage,
  evolvingPromptMessage,
} from './evolutionChoice.js';
import { playMoveEffect } from './effects/moveEffects.js';
import { Actor, playShowdownAnim, NEAR_Z, FAR_Z } from './effects/showdownFx.js';
import { getShowdownAnim } from './effects/showdownMoves.js';
import { moveMenuLayout } from './battleMoveMenu.js';

// ── Launch contract (built by Route / Town) ─────────────────────────────────────

/**
 * What a caller passes when starting the Battle scene. The player's party comes from
 * the session; the caller supplies the opponent + where to return.
 */
export interface BattleSceneData {
  /** 'wild' (catchable) or 'friendly' (no-stakes sparring). */
  readonly kind: BattleKind;
  /** The opponent's party (a single wild mon, or a trainer's team). */
  readonly opponentParty: readonly PokemonInstance[];
  /** Display name for the opponent side ('Wild', or the SimTrainer username). */
  readonly opponentName?: string;
  /** Scene key to return to when the battle finishes (default 'Town'). */
  readonly returnScene?: string;
  /** Data to pass back to the return scene. */
  readonly returnData?: Record<string, unknown>;
  /** Override the session's Cozy↔Spicy dial for this battle (optional). */
  readonly difficultyOverride?: Difficulty;
  /**
   * Per-battle EXP generosity override (Training Grounds cranks this up so deliberate
   * grinding feels worth it). Forwarded to expGainFromBattle; when absent, the engine's
   * default generous yield applies (CLAUDE.md pillar 6).
   */
  readonly trainingExpGenerosity?: number;
}

// ── Animation pacing ─────────────────────────────────────────────────────────────

const PACE_NORMAL = 1;
const PACE_TURBO = 0.28;

/** Base dwell (ms) for a plain message event at normal pace. */
const MESSAGE_DWELL = 900;
/** HP-bar drain tween duration (ms) at normal pace. */
const HP_DRAIN_MS = 520;

/**
 * The classic cream info box (name + Lv + HP gauge). Hoisted to module scope so the
 * layout can place the card precisely within the safe area (no magic half-widths) and
 * keep the player nameplate clear of the bottom command menu on a narrow phone.
 */
const CARD_W = 230;
const CARD_H = 72;

/** A worked-out participant's live, derived display facts. */
interface SideView {
  panel: Phaser.GameObjects.Container;
  /** A Sprite (not Image) so a 3D render can amble via a looping idle animation. */
  sprite: Phaser.GameObjects.Sprite;
  nameText: Phaser.GameObjects.Text;
  levelText: Phaser.GameObjects.Text;
  /** The classic green→yellow→red HP gauge (owns its own cur/max readout). */
  hpBar: HPBar;
  /**
   * The thin classic-blue EXP gauge under the HP bar — PLAYER side only (the cartridge
   * shows EXP for the mon in front). Drawn manually so it can wrap on a level-up. Null
   * on the opponent card.
   */
  expBar: Phaser.GameObjects.Graphics | null;
  /** Last-drawn EXP fraction (0..1) for smooth fills. */
  expFrac: number;
  statusBadge: Phaser.GameObjects.Text;
  /** Last-rendered HP fraction (for smooth drains). */
  shownHp: number;
  /** The dex number whose sprite is currently shown (so we only reload on change). */
  shownDex: number;
}

export class BattleScene extends Phaser.Scene {
  private layout!: Layout;
  private index!: DataIndex;
  private resolver!: SpriteResolver;
  private session!: GameSession;
  private reduced = false;
  /**
   * Whether the Showdown battle-fx textures finished loading in Preload. When true (and
   * not reduced-motion), {@link playMoveVfx} plays the ported Showdown move animation;
   * otherwise it falls back to the procedural {@link playMoveEffect}. Read once in create.
   */
  private fxReady = false;

  private params!: BattleSceneData;
  private deps!: BattleDeps;
  private state!: BattleState;

  // Render objects
  private fieldNear?: Phaser.GameObjects.Graphics;
  private playerView!: SideView;
  private oppView!: SideView;
  private dialog!: DialogBox;
  /** The currently-open classic selection box (action / move / switch / bag), if any. */
  private menu?: Menu;
  private turboButton?: Button;

  // Turn flow
  private pace = PACE_NORMAL;
  private busy = false;
  private finished = false;
  /** Participation tracking for whole-team EXP (instance ids that took the field). */
  private participants = new Set<string>();
  /** Species the player saw this battle (for the dex). */
  private seen = new Set<string>();
  private caughtMon: PokemonInstance | null = null;
  /** The winning side once the battle concludes (for the Training-loop return beat). */
  private lastWinner: BattleSideId | 'draw' | null = null;

  constructor() {
    super('Battle');
  }

  init(data: Partial<BattleSceneData>): void {
    // Defensive defaults: a tired player must never hit a crash, even if a caller
    // somehow launches the scene without a fully-formed payload.
    this.params = {
      kind: data.kind ?? 'wild',
      opponentParty: data.opponentParty ?? [],
      ...(data.opponentName !== undefined ? { opponentName: data.opponentName } : {}),
      ...(data.returnScene !== undefined ? { returnScene: data.returnScene } : {}),
      ...(data.returnData !== undefined ? { returnData: data.returnData } : {}),
      ...(data.difficultyOverride !== undefined
        ? { difficultyOverride: data.difficultyOverride }
        : {}),
      ...(data.trainingExpGenerosity !== undefined
        ? { trainingExpGenerosity: data.trainingExpGenerosity }
        : {}),
    };
    this.pace = PACE_NORMAL;
    this.busy = false;
    this.finished = false;
    this.participants = new Set();
    this.seen = new Set();
    this.caughtMon = null;
    this.lastWinner = null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();
    this.fxReady = this.registry.get(REGISTRY_FX_READY) === true;
    this.session = getSession(this);
    this.index =
      (this.registry.get(REGISTRY_DATA_INDEX) as DataIndex | undefined) ?? this.sessionIndex();
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolver(this);
    // Layer the Cozy↔Spicy combat handicap onto the session's faithful engine deps.
    // In Cozy the wild hits softer, takes more, and is easier to catch; Mild/Spicy
    // are neutral. The level cap is applied upstream (Route, when the foe is built);
    // these touch only the in-battle multipliers (core/difficulty is the source).
    const diff = this.difficulty();
    this.deps = {
      ...this.session.battleDeps,
      foeDamageMultiplier: foeDamageMultiplier(diff),
      playerDamageMultiplier: playerDamageMultiplier(diff),
      catchRateMultiplier: catchRateMultiplier(diff),
    };

    this.buildState();
    this.buildField();
    this.playerView = this.makeSideView(/*withExpBar*/ true);
    this.oppView = this.makeSideView(/*withExpBar*/ false);
    this.buildDialog();
    this.buildTurboToggle();

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.refreshSprites();
    this.refreshSideView(this.playerView, 'player', /*instant*/ true);
    this.refreshSideView(this.oppView, 'opponent', /*instant*/ true);

    fadeIn(this);
    this.openingMessage();
  }

  // ── State construction ───────────────────────────────────────────────────────

  private buildState(): void {
    const team = this.session.getTeam();
    const playerParty = team.length > 0 ? team : [this.fallbackMon()];
    const playerSide: BattleSide = {
      id: 'player',
      trainerName: this.session.trainerName,
      party: playerParty.map(toParticipant),
      activeIndex: firstUsableIndex(playerParty),
    };
    const oppParty =
      this.params.opponentParty.length > 0 ? this.params.opponentParty : [this.fallbackMon()];
    const oppSide: BattleSide = {
      id: 'opponent',
      trainerName: this.params.opponentName ?? (this.params.kind === 'wild' ? 'Wild' : 'Trainer'),
      party: oppParty.map(toParticipant),
      activeIndex: firstUsableIndex(oppParty),
    };

    const seed =
      (this.session.seed ^
        hashString(`battle|${this.session.currentDate}|${oppParty[0]?.id ?? 'x'}`)) >>>
      0;
    this.state = startBattle(this.params.kind, playerSide, oppSide, seed, this.deps);

    // Track who's on the field + seen species.
    this.noteActive('player');
    this.noteActive('opponent');
  }

  private noteActive(side: BattleSideId): void {
    const p = activeOf(this.state.sides[side]);
    if (!p) return;
    if (side === 'player') this.participants.add(p.instanceId);
    else this.seen.add(p.instance.speciesId);
  }

  // ── Calm placeholder environment ───────────────────────────────────────────────

  private buildField(): void {
    // A soft layered ground: a sky-ish gradient band + two gentle platform ovals.
    this.fieldNear = this.add.graphics();
  }

  private drawField(): void {
    const g = this.fieldNear;
    if (!g) return;
    const safe = this.layout.safe;
    g.clear();
    // Soft horizon band (top calm, lower warm) — programmatic, swappable.
    g.fillStyle(COLORS.surface, 1);
    g.fillRect(safe.x, safe.y, safe.width, safe.height);
    g.fillStyle(COLORS.surfaceAlt, 0.6);
    g.fillRect(safe.x, safe.y + safe.height * 0.52, safe.width, safe.height * 0.48);

    // Two grounding ellipses under the combatants (drawn at layout time).
    const oppPos = this.combatantPos('opponent');
    const plPos = this.combatantPos('player');
    g.fillStyle(COLORS.scrim, 0.18);
    g.fillEllipse(oppPos.x, oppPos.y + 34, 132, 34);
    g.fillEllipse(plPos.x, plPos.y + 40, 168, 40);
  }

  // ── Side views (sprite + HP card) ──────────────────────────────────────────────

  private makeSideView(withExpBar: boolean): SideView {
    const panel = this.add.container(0, 0);

    // The classic cream info box with a dark double border (name + Lv on top, the
    // green→yellow→red HP gauge below) — a tasteful GB/GBC nod over the readable font.
    const cardW = CARD_W;
    const cardH = CARD_H;
    const r = RADIUS.md;
    const cardGfx = this.add.graphics();
    cardGfx.fillStyle(COLORS.scrim, 0.22);
    cardGfx.fillRoundedRect(-cardW / 2 + 2, -cardH / 2 + 4, cardW, cardH, r);
    cardGfx.fillStyle(COLORS.boxFill, 1);
    cardGfx.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, r);
    cardGfx.lineStyle(3, COLORS.boxBorder, 1);
    cardGfx.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, r);
    cardGfx.lineStyle(1, COLORS.boxBorderInner, 1);
    cardGfx.strokeRoundedRect(
      -cardW / 2 + 4,
      -cardH / 2 + 4,
      cardW - 8,
      cardH - 8,
      Math.max(2, r - 4),
    );

    const nameText = this.add.text(-cardW / 2 + SPACING.md, -cardH / 2 + SPACING.sm, '', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.boxText,
      fontStyle: 'bold',
    });
    const levelText = this.add
      .text(cardW / 2 - SPACING.md, -cardH / 2 + SPACING.sm, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.boxTextMuted,
      })
      .setOrigin(1, 0);

    // The shared, unit-tested HP gauge (its own "HP" tag + cur/max numbers).
    const barWidth = cardW - SPACING.md * 2;
    const hpBar = new HPBar(this, -barWidth / 2, 4, {
      width: barWidth,
      showNumbers: true,
      fontSize: FONT.size.xs,
    });

    const statusBadge = this.add
      .text(-cardW / 2 + SPACING.md, cardH / 2 - SPACING.sm, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.bg,
        backgroundColor: hexToCss(COLORS.warm),
        padding: { x: 5, y: 1 },
      })
      .setOrigin(0, 1)
      .setVisible(false);

    panel.add([cardGfx, nameText, levelText, hpBar, statusBadge]);

    // The thin classic-blue EXP gauge, pinned along the card's bottom inner edge —
    // PLAYER side only (the cartridge shows EXP for the mon in front). Drawn manually
    // so it can wrap on a level-up.
    let expBar: Phaser.GameObjects.Graphics | null = null;
    if (withExpBar) {
      expBar = this.add.graphics();
      panel.add(expBar);
    }

    // The combatant sprite (player = back, opponent = front — set in loadSprite). A
    // Sprite (not Image) so a 3D-model render can play a gentle looping idle.
    const sprite = this.add.sprite(0, 0, '__MISSING').setVisible(false);
    sprite.setOrigin(0.5, 1);

    return {
      panel,
      sprite,
      nameText,
      levelText,
      hpBar,
      expBar,
      expFrac: 0,
      statusBadge,
      shownHp: 1,
      shownDex: -1,
    };
  }

  /** Height (px) of the thin EXP gauge. */
  private static readonly EXP_BAR_H = 4;

  /** Redraw a side's EXP gauge (no-op on a side without one, e.g. the opponent). */
  private drawExpBar(view: SideView): void {
    const g = view.expBar;
    if (!g) return;
    const pad = SPACING.md;
    const x = -CARD_W / 2 + pad;
    const w = CARD_W - pad * 2;
    const h = BattleScene.EXP_BAR_H;
    const y = CARD_H / 2 - h - 5;
    const r = 2;
    g.clear();
    g.fillStyle(COLORS.hpTrack, 1);
    g.fillRoundedRect(x, y, w, h, r);
    const fillW = w * Math.max(0, Math.min(1, view.expFrac));
    if (fillW > 1) {
      g.fillStyle(COLORS.accent, 1);
      g.fillRoundedRect(x, y, fillW, h, r);
    }
    g.lineStyle(1, COLORS.hpFrame, 1);
    g.strokeRoundedRect(x, y, w, h, r);
  }

  /** Set the player EXP gauge fraction instantly (used by the fill tween + wraps). */
  private setPlayerExpFrac(frac: number): void {
    const v = this.playerView;
    v.expFrac = Math.max(0, Math.min(1, frac));
    this.drawExpBar(v);
  }

  /** Smoothly fill the player EXP gauge from → to (instant under reduced-motion). */
  private tweenPlayerExpFrac(from: number, to: number): Promise<void> {
    const v = this.playerView;
    if (this.reduced || !v.expBar) {
      this.setPlayerExpFrac(to);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const proxy = { f: Math.max(0, Math.min(1, from)) };
      this.setPlayerExpFrac(proxy.f);
      this.tweens.add({
        targets: proxy,
        f: Math.max(0, Math.min(1, to)),
        duration: Math.max(90, 540 * this.pace),
        ease: 'Sine.easeOut',
        onUpdate: () => this.setPlayerExpFrac(proxy.f),
        onComplete: () => {
          this.setPlayerExpFrac(to);
          resolve();
        },
      });
    });
  }

  /**
   * Animate the player EXP gauge from an old (level, exp) to a new one, WRAPPING to 0 on
   * each level crossed (the classic fill-to-full-then-reset). Reduced-motion snaps to the
   * final fraction.
   */
  private async animatePlayerExpBar(
    species: SpeciesData,
    fromLevel: number,
    fromExp: number,
    toLevel: number,
    toExp: number,
  ): Promise<void> {
    const rate = species.growthRate;
    if (this.reduced || !this.playerView.expBar) {
      this.setPlayerExpFrac(expBarFraction(rate, toLevel, toExp));
      return;
    }
    let startFrac = expBarFraction(rate, fromLevel, fromExp);
    for (let lvl = fromLevel; lvl < toLevel; lvl++) {
      await this.tweenPlayerExpFrac(startFrac, 1);
      startFrac = 0;
      this.setPlayerExpFrac(0);
    }
    await this.tweenPlayerExpFrac(startFrac, expBarFraction(rate, toLevel, toExp));
  }

  /** Re-resolve the active mons' sprites (only reloads when the species changes). */
  private refreshSprites(): void {
    void this.loadSprite('player');
    void this.loadSprite('opponent');
  }

  private async loadSprite(side: BattleSideId): Promise<void> {
    const view = side === 'player' ? this.playerView : this.oppView;
    const active = activeOf(this.state.sides[side]);
    if (!active) return;
    const species = this.speciesOf(active.instance.speciesId);
    const dex = species?.dexNo ?? 0;
    if (dex === view.shownDex) return;
    view.shownDex = dex;
    const facing: 'front' | 'back' = side === 'player' ? 'back' : 'front';
    // Player back-sprite is the hero, drawn large — but on a NARROW phone the info card is
    // pinned to the right edge (it can't move further right) and the field is too short to
    // lift the card into the gap between the two sprites. So on compact we trim the player
    // sprite a touch (still big + satisfying) which, together with tucking it into the
    // lower-LEFT (see combatantPos), keeps its right edge clear of the nameplate's left
    // edge so the FULL name reads (was clipping the first letter → "ulbasaur"). Desktop is
    // unchanged (roomy layout never overlapped).
    const playerMax = this.layout.isCompact ? 132 : 150;
    const maxSide = side === 'player' ? playerMax : 124;

    // PREFER the 3D-model render (animated): foe = front, player lead = back. Each side
    // falls back independently to the Gen5 battle chain (animated -> static -> official
    // -> placeholder) when its render is absent — never a crash, never a blank.
    const render = await this.resolver.renderBattle(dex, facing).catch(() => null);
    if (!this.scene.isActive() || view.shownDex !== dex) return;
    if (render) {
      view.sprite.stop();
      view.sprite.setTexture(render.textureKey, 0);
      const scale = Math.min(
        maxSide / (render.frameWidth || 1),
        maxSide / (render.frameHeight || 1),
        3.5,
      );
      view.sprite.setScale(scale).setVisible(true);
      if (!this.reduced) view.sprite.play({ key: render.animKey, startFrame: 0 });
      this.positionSprite(side);
      return;
    }

    const resolved = await this.resolver.battle(dex, facing);
    if (!resolved || !this.scene.isActive() || view.shownDex !== dex) return;
    view.sprite.stop();
    view.sprite.setTexture(resolved.key);
    const w = view.sprite.width || 1;
    const h = view.sprite.height || 1;
    const scale = Math.min(maxSide / w, maxSide / h, 3.5);
    view.sprite.setScale(scale).setVisible(true);
    this.positionSprite(side);
  }

  private positionSprite(side: BattleSideId): void {
    const view = side === 'player' ? this.playerView : this.oppView;
    const pos = this.combatantPos(side);
    view.sprite.setPosition(pos.x, pos.y + 34);
  }

  /** Refresh a side's HP card from current engine state (smooth unless instant). */
  private refreshSideView(view: SideView, side: BattleSideId, instant = false): void {
    const active = activeOf(this.state.sides[side]);
    if (!active) return;
    const inst = active.instance;
    const species = this.speciesOf(inst.speciesId);
    const display = inst.nickname ?? species?.name ?? inst.speciesId;
    view.nameText.setText(display);
    view.levelText.setText(`Lv ${inst.level}`);
    const frac = Math.max(0, Math.min(1, inst.currentHp / Math.max(1, inst.maxHp)));
    // Keep the cur/max readout truthful immediately; tween only the gauge fill.
    view.hpBar.setHp(Math.max(0, inst.currentHp), inst.maxHp);
    this.setStatusBadge(view, inst.status);
    // The player's EXP gauge tracks the mon currently in front.
    if (side === 'player' && view.expBar) {
      view.expFrac = species ? expBarFraction(species.growthRate, inst.level, inst.exp) : 0;
      this.drawExpBar(view);
    }
    if (instant || this.reduced) {
      view.shownHp = frac;
      view.hpBar.setRatio(frac);
    } else {
      view.hpBar.tweenTo(frac, HP_DRAIN_MS * this.pace, this.reduced);
      view.shownHp = frac;
    }
  }

  private setStatusBadge(view: SideView, status: StatusCondition): void {
    if (status === 'none') {
      view.statusBadge.setVisible(false);
      return;
    }
    const labels: Record<string, { t: string; c: number }> = {
      paralysis: { t: 'PAR', c: COLORS.warm },
      burn: { t: 'BRN', c: COLORS.rose },
      poison: { t: 'PSN', c: 0xc6a6e9 },
      toxic: { t: 'TOX', c: 0xc6a6e9 },
      sleep: { t: 'SLP', c: COLORS.textMuted },
      freeze: { t: 'FRZ', c: COLORS.accent },
    };
    const meta = labels[status] ?? { t: status.slice(0, 3).toUpperCase(), c: COLORS.warm };
    view.statusBadge.setText(meta.t).setBackgroundColor(hexToCss(meta.c)).setVisible(true);
  }

  // ── Dialog + opening ───────────────────────────────────────────────────────────

  private buildDialog(): void {
    this.dialog = new DialogBox(this, 0, 0, {
      width: 320,
      height: 96,
      reducedMotion: this.reduced,
    });
    this.dialog.setVisible(false);
  }

  private openingMessage(): void {
    const oppActive = activeOf(this.state.sides.opponent);
    const oppName = oppActive ? this.displayName(oppActive) : 'a Pokémon';
    const msg =
      this.params.kind === 'wild'
        ? `A wild ${oppName} appeared!`
        : `${this.state.sides.opponent.trainerName} wants to battle!`;
    this.showMessage(msg, () => this.openMenu());
  }

  /**
   * Show a one-off message, then run `after` once it has dwelt. Calm + automatic
   * (no tap required) so a tired player never has to fish for the right pixel.
   */
  private showMessage(text: string, after?: () => void): void {
    this.hideMenu();
    void this.message(text).then(() => {
      if (this.scene.isActive()) after?.();
    });
  }

  // ── The action menu (classic ▶-cursor selection box, touch-first) ──────────────

  private openMenu(): void {
    if (this.finished) return;
    this.dialog.setVisible(false);
    this.buildMainMenu();
  }

  private hideMenu(): void {
    this.menu?.destroy();
    this.menu = undefined;
  }

  /**
   * Open a classic Pokémon selection box: a cream/dark-border Menu with a ▶ cursor,
   * keyboard + touch, anchored just above the bottom message box. `columns: 2` gives
   * the FIGHT 2×2 grid feel; a list otherwise.
   */
  private showMenu(
    items: readonly MenuItem[],
    opts: {
      columns?: number;
      width?: number;
      fontSize?: number;
      maxVisibleRows?: number;
      onCancel?: () => void;
    } = {},
  ): void {
    this.hideMenu();
    const menu = new Menu(this, 0, 0, {
      items,
      reducedMotion: this.reduced,
      ...(opts.columns !== undefined ? { columns: opts.columns } : {}),
      ...(opts.width !== undefined ? { width: opts.width } : {}),
      ...(opts.fontSize !== undefined ? { fontSize: opts.fontSize } : {}),
      ...(opts.maxVisibleRows !== undefined ? { maxVisibleRows: opts.maxVisibleRows } : {}),
      ...(opts.onCancel !== undefined ? { onCancel: opts.onCancel } : {}),
    });
    this.menu = menu;
    this.positionMenu();
    menu.focus();
  }

  private buildMainMenu(): void {
    // FIGHT / POKéMON / BAG / RUN — the iconic four, in a 2×2 grid.
    this.showMenu(
      [
        { label: 'Fight', onSelect: () => this.openMoveMenu() },
        { label: 'Pokémon', onSelect: () => this.openSwitchMenu() },
        { label: 'Bag', onSelect: () => this.openBagMenu() },
        { label: 'Run', onSelect: () => this.chooseRun() },
      ],
      { columns: 2 },
    );
  }

  private openMoveMenu(): void {
    const active = activeOf(this.state.sides.player);
    const moves = active?.instance.moves ?? [];
    const defender = activeOf(this.state.sides.opponent);
    const defTypes = defender ? this.speciesOf(defender.instance.speciesId)?.types : undefined;

    // UNLIMITED MOVES: show EVERY move, sorted most-used → least-used (ties keep learn
    // order). We keep each slot's ORIGINAL index so chooseMove → resolveTurn still targets
    // the right `instance.moves[moveSlot]` after the display is re-sorted.
    const ordered = moves
      .map((slot, i) => ({ slot, i }))
      .sort((a, b) => moveUses(b.slot) - moveUses(a.slot) || a.i - b.i);

    const items: MenuItem[] = ordered.map(({ slot, i }) => {
      const move = this.moveOf(slot.moveId);
      const usable = slot.pp > 0 && !!move;
      const eff = move && defTypes ? effectivenessAgainst(move.type, defTypes) : 1;
      const effLabel = effectivenessLabel(eff);
      // Right-aligned hint carries the PP + a short effectiveness cue (classic feel).
      const hint = `PP ${slot.pp}/${slot.maxPp}${effLabel.text ? `  ${effLabel.text}` : ''}`;
      return {
        label: move?.name ?? '—',
        hint,
        disabled: !usable,
        onSelect: () => this.chooseMove(i),
      };
    });
    items.push({ label: 'Back', onSelect: () => this.buildMainMenu() });

    // 2×2 grid on desktop/roomy; single safe-width column on a narrow portrait phone so
    // long move names can't clip off the left edge. With an unbounded moveset the list can
    // exceed the screen, so force a SINGLE SCROLLABLE column once there are more than four
    // moves (no grid scroll) — the menu clips to a few rows and the cursor / drag / wheel
    // page through the rest. See moveMenuLayout() + Menu.maxVisibleRows.
    const lay = moveMenuLayout({
      safeWidth: this.layout.safe.width,
      isCompact: this.layout.isCompact,
      isPortrait: this.layout.isPortrait,
    });
    const manyMoves = moves.length > 4;
    const columns = manyMoves ? 1 : lay.columns;
    // Cap visible rows to what fits comfortably above the message box (small on a short
    // phone). +1 leaves room so the trailing "Back" row is reachable by scroll.
    const visibleRows = this.moveMenuVisibleRows();
    this.showMenu(items, {
      columns,
      ...(lay.width !== undefined ? { width: lay.width } : {}),
      ...(columns === 1 && lay.width === undefined ? { width: this.moveMenuWidth() } : {}),
      ...(lay.fontSize !== undefined ? { fontSize: lay.fontSize } : {}),
      ...(columns === 1 && items.length > visibleRows ? { maxVisibleRows: visibleRows } : {}),
      onCancel: () => this.buildMainMenu(),
    });
  }

  /** Rows the scrollable move menu shows before clipping — sized to the band above the box. */
  private moveMenuVisibleRows(): number {
    const safe = this.layout.safe;
    const band = safe.height - this.dialogHeight() - SPACING.md * 2;
    const fit = Math.floor(band / TOUCH_TARGET);
    // Always show at least 3, never more than 6 (calm, classic-feeling window).
    return Math.max(3, Math.min(6, fit));
  }

  /** A safe single-column width for the move menu when the layout didn't dictate one. */
  private moveMenuWidth(): number {
    return Math.min(this.layout.safe.width - SPACING.md * 2, 360);
  }

  private openSwitchMenu(): void {
    const party = this.state.sides.player.party;
    const activeIdx = this.state.sides.player.activeIndex;
    const items: MenuItem[] = party.map((p, i) => {
      const inst = p.instance;
      const species = this.speciesOf(inst.speciesId);
      const fainted = inst.currentHp <= 0;
      const isActive = i === activeIdx;
      const status = inst.status !== 'none' ? ` ${inst.status.slice(0, 3).toUpperCase()}` : '';
      const tag = isActive ? '  (out)' : '';
      return {
        label: `${inst.nickname ?? species?.name ?? inst.speciesId}  Lv${inst.level}`,
        hint: `${Math.max(0, inst.currentHp)}/${inst.maxHp}${status}${tag}`,
        disabled: fainted || isActive,
        onSelect: () => this.chooseSwitch(i),
      };
    });
    items.push({ label: 'Back', onSelect: () => this.buildMainMenu() });
    this.showMenu(items, { onCancel: () => this.buildMainMenu() });
  }

  private openBagMenu(): void {
    if (this.params.kind !== 'wild') {
      this.showMessage('This is a friendly spar — best to keep the balls in your bag.', () =>
        this.openMenu(),
      );
      return;
    }
    const balls: { id: string; label: string }[] = [
      { id: 'poke-ball', label: 'Poké Ball' },
      { id: 'great-ball', label: 'Great Ball' },
      { id: 'ultra-ball', label: 'Ultra Ball' },
      { id: 'master-ball', label: 'Master Ball' },
    ];
    const items: MenuItem[] = balls.map((ball) => ({
      label: ball.label,
      onSelect: () => this.chooseBall(ball.id),
    }));
    items.push({ label: 'Back', onSelect: () => this.buildMainMenu() });
    this.showMenu(items, { onCancel: () => this.buildMainMenu() });
  }

  // ── Action handlers → resolve a turn ─────────────────────────────────────────────

  private chooseMove(slot: number): void {
    // Tally the pick so the move menu can surface favourites first, and persist it onto
    // the SESSION mon (the battle state holds a clone — see toParticipant) so the count
    // survives the battle. moveSlot still indexes instance.moves for resolveTurn.
    const active = activeOf(this.state.sides.player);
    const moveSlot = active?.instance.moves[slot];
    if (active && moveSlot) {
      moveSlot.uses = (moveSlot.uses ?? 0) + 1;
      this.session.recordMoveUse(active.instanceId, moveSlot.moveId);
    }
    this.commitTurn({ type: 'move', moveSlot: slot });
  }

  private chooseSwitch(partyIndex: number): void {
    this.commitTurn({ type: 'switch', partyIndex });
  }

  private chooseBall(itemId: string): void {
    this.commitTurn({ type: 'item', itemId });
  }

  private chooseRun(): void {
    if (this.params.kind !== 'wild') {
      // Leaving a friendly spar is always fine and warm — conclude gently.
      if (this.finished) return;
      this.finished = true;
      this.hideMenu();
      void this.endBattleMessage('draw');
      return;
    }
    this.commitTurn({ type: 'run' });
  }

  /** Resolve one turn against an AI-chosen opponent action, then animate it. */
  private commitTurn(playerAction: BattleAction): void {
    if (this.busy || this.finished) return;
    this.busy = true;
    this.hideMenu();

    const oppAction = this.chooseOpponentAction();
    const rng = this.turnRng();
    const result = resolveTurn(this.state, playerAction, oppAction, rng, this.deps);
    this.state = result.state;
    void this.playEvents(result.events);
  }

  private turnRng(): Rng {
    return makeRng((this.state.rngSeed ^ hashString(`turn|${this.state.turn}`)) >>> 0).fork('turn');
  }

  // ── Opponent AI (honors Cozy↔Spicy) ────────────────────────────────────────────

  private chooseOpponentAction(): BattleAction {
    const active = activeOf(this.state.sides.opponent);
    if (!active) return { type: 'run' };
    const moves = active.instance.moves
      .map((m, i) => ({ m, i }))
      .filter((x) => x.m.pp > 0 && this.moveOf(x.m.moveId));
    if (moves.length === 0) return { type: 'move', moveSlot: 0 };

    const params = DIFFICULTIES[this.difficulty()];
    const ai = makeRng((this.state.rngSeed ^ hashString(`ai|${this.state.turn}`)) >>> 0);

    // The "smart" branch (probability = aiQuality): pick the highest-expected-damage
    // move against the player's active mon. Otherwise pick at random — gentle by design.
    if (ai.float() < params.aiQuality) {
      const defender = activeOf(this.state.sides.player);
      const defTypes = defender ? this.speciesOf(defender.instance.speciesId)?.types : undefined;
      let best = moves[0]!;
      let bestScore = -1;
      for (const cand of moves) {
        const move = this.moveOf(cand.m.moveId)!;
        const power = move.power ?? 0;
        const eff = defTypes ? effectivenessAgainst(move.type, defTypes) : 1;
        const score = power * eff + (move.category === 'status' ? 5 : 0);
        if (score > bestScore) {
          bestScore = score;
          best = cand;
        }
      }
      return { type: 'move', moveSlot: best.i };
    }
    const pick = moves[ai.int(moves.length)]!;
    return { type: 'move', moveSlot: pick.i };
  }

  private difficulty(): Difficulty {
    return this.params.difficultyOverride ?? this.session.currentSettings.difficulty;
  }

  // ── Event animation (the heart of the calm playback) ─────────────────────────────

  private async playEvents(events: readonly BattleEvent[]): Promise<void> {
    for (const ev of events) {
      if (!this.scene.isActive()) return;
      await this.playEvent(ev);
    }
    if (this.scene.isActive()) this.afterEvents();
  }

  private async playEvent(ev: BattleEvent): Promise<void> {
    switch (ev.kind) {
      case 'turn-start':
        return;
      case 'switch': {
        if (ev.side === 'opponent' || ev.side === 'player') {
          this.noteActive(ev.side);
          await this.loadSprite(ev.side);
          this.refreshSideView(this.viewFor(ev.side), ev.side, true);
          const name = this.activeName(ev.side);
          await this.message(
            ev.side === 'player' ? `Go, ${name}!` : `${this.oppLabel()} sent out ${name}!`,
          );
        }
        return;
      }
      case 'move-used': {
        const name = this.instanceNameById(ev.side, ev.instanceId);
        const move = this.moveOf(ev.moveId);
        await this.lunge(ev.side);
        await this.message(`${name} used ${move?.name ?? ev.moveId}!`);
        // Procedural move VFX (type/category-coloured beam, lunge, or aura). We await it
        // BEFORE any following 'damage' event drains HP, so the effect plays and *then*
        // the gauge falls. Status moves (no damage to follow) still get their user aura.
        // TURBO is honoured via `this.pace`; reduced-motion via `this.reduced`. The engine
        // is self-cleaning + teardown-safe, so this stays re-entry safe.
        await this.playMoveVfx(ev.side, move, ev.moveId);
        return;
      }
      case 'move-missed':
        await this.message(`${this.instanceNameById(ev.side, ev.instanceId)}'s attack missed!`);
        return;
      case 'damage': {
        this.refreshSideView(this.viewFor(ev.side), ev.side);
        await this.flashHit(ev.side);
        await this.wait(HP_DRAIN_MS);
        if (ev.critical) await this.message('A critical hit!');
        const note = effectivenessNote(ev.effectiveness);
        if (note) await this.message(note);
        return;
      }
      case 'heal':
        this.refreshSideView(this.viewFor(ev.side), ev.side);
        await this.wait(HP_DRAIN_MS);
        return;
      case 'status-applied':
        this.refreshSideView(this.viewFor(ev.side), ev.side);
        await this.message(
          `${this.instanceNameById(ev.side, ev.instanceId)} was ${statusVerb(ev.status)}!`,
        );
        return;
      case 'stat-change': {
        const dir = ev.stages > 0 ? 'rose' : 'fell';
        await this.message(
          `${this.instanceNameById(ev.side, ev.instanceId)}'s ${statName(ev.stat)} ${dir}!`,
        );
        return;
      }
      case 'faint': {
        const name = this.instanceNameById(ev.side, ev.instanceId);
        await this.faintDrop(ev.side);
        await this.message(`${name} fainted!`);
        return;
      }
      case 'catch-attempt':
        if (ev.caught) {
          const wild = activeOf(this.state.sides.opponent);
          if (wild) {
            this.caughtMon = freshenCaught(wild.instance, this.session.trainerName);
            this.seen.add(this.caughtMon.speciesId);
          }
        }
        await this.playCatch(ev.shakes, ev.caught);
        return;
      case 'message':
        await this.message(ev.text);
        return;
      case 'battle-end':
        // Conclusion (post-battle flourishes) runs after the stream drains; see
        // afterEvents → concludeBattle. The winner is read off the final state there.
        return;
      default:
        return;
    }
  }

  /**
   * What to do once a turn's events finished playing. The engine auto-sends the next
   * usable party member on a faint (and the `switch` event already repainted it), so
   * here we either conclude or re-open the menu for the next turn.
   */
  private afterEvents(): void {
    if (this.state.finished) {
      void this.concludeBattle();
      return;
    }
    this.busy = false;
    this.refreshSideView(this.playerView, 'player', true);
    this.refreshSideView(this.oppView, 'opponent', true);
    this.openMenu();
  }

  // ── Conclusion + post-battle pipeline ─────────────────────────────────────────────

  private async concludeBattle(): Promise<void> {
    if (this.finished) return;
    this.finished = true;
    this.hideMenu();

    const winner = this.state.winner ?? 'draw';
    this.lastWinner = winner;

    // A catch is its own warm conclusion: Gotcha → dex → nickname → EXP (modern games
    // grant a catch the same EXP a KO would) → team/box. It persists the caught mon.
    if (this.caughtMon) {
      await this.runCatchConclusion();
      this.endBattle();
      return;
    }

    // EXP + EV + friendship are earned on a genuine KO victory only — never on a friendly
    // spar (no stakes). The growth pipeline persists the updated team + seen species.
    const earnedExp = winner === 'player' && this.params.kind !== 'friendly';
    if (earnedExp) {
      await this.applyBattleGrowth({ awardEv: true, winBonus: true });
    }

    await this.endBattleMessage(winner);
  }

  /**
   * The warm catch conclusion (see {@link concludeBattle}). The catch VFX already played
   * during the event stream; here we say Gotcha, register the dex on a first catch, offer
   * an optional nickname, award catch-EXP (modern behavior) + a little friendship, and —
   * if the party was full — tell the player it went to Box 1. Persists the caught mon.
   */
  private async runCatchConclusion(): Promise<void> {
    const mon = this.caughtMon;
    if (!mon) return;
    const species = this.speciesOf(mon.speciesId);
    const speciesName = species?.name ?? mon.speciesId;

    await this.lingerMessage(`Gotcha! ${speciesName} was caught!`);

    // Dex-registration beat — only the FIRST time this species is caught.
    if (!this.session.hasCaught(mon.speciesId)) {
      await this.lingerMessage(`${speciesName}'s data was added to the Pokédex!`);
    }

    // Optional nickname (Enter/skip keeps the species name).
    const nick = await this.askNickname(speciesName);
    if (nick) this.caughtMon = { ...mon, nickname: nick };

    // Was the party already full? (Checked BEFORE persist places the mon.)
    const partyFull = this.session.getTeam().length >= 6;

    // A catch grants the same EXP a KO would (+ a little friendship); no EVs (not a KO).
    await this.applyBattleGrowth({ awardEv: false, winBonus: true });

    if (partyFull) {
      const shown = this.caughtMon?.nickname ?? speciesName;
      await this.lingerMessage(`${shown} was sent to Box 1!`);
    }
  }

  private async applyBattleGrowth(opts: { awardEv: boolean; winBonus: boolean }): Promise<void> {
    // Sum EXP from each opponent mon the player defeated this battle. We approximate
    // "defeated" as the opponent party (a wild battle has one; a trainer battle's whole
    // team was beaten to win). Generous + whole-team (pillar 6).
    const team = this.session.getTeam();
    const memberIds = team.map((m) => m.id);
    const totalAwards = new Map<string, number>();

    for (const foe of this.params.opponentParty) {
      const species = this.speciesOf(foe.speciesId);
      if (!species) continue;
      const awards = expGainFromBattle(
        { baseExpYield: species.baseExpYield, defeatedLevel: foe.level },
        memberIds.map((id) => ({
          instanceId: id,
          participated: this.participants.has(id),
          fainted: team.find((t) => t.id === id)!.currentHp <= 0,
        })),
        DEFAULT_EXP_SHARE,
        // Training waves crank the per-battle generosity; normal play uses the engine
        // default (passing undefined keeps expGainFromBattle's EXP_GENEROSITY default).
        this.params.trainingExpGenerosity,
      );
      for (const a of awards) {
        totalAwards.set(a.instanceId, (totalAwards.get(a.instanceId) ?? 0) + a.exp);
      }
    }

    // The combined EV yield of every defeated foe (real mechanic) — awarded to each
    // PARTICIPANT on a genuine KO, clamped to the 252/510 caps. Quiet: no UI (the Team
    // detail screens already surface EVs).
    const evGain: Record<StatKey, number> = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
    if (opts.awardEv) {
      for (const foe of this.params.opponentParty) {
        const species = this.speciesOf(foe.speciesId);
        if (!species) continue;
        const y = evYieldOf(species);
        for (const k of STAT_KEYS) evGain[k] += y[k];
      }
    }
    const anyEv = STAT_KEYS.some((k) => evGain[k] > 0);

    const activeId = activeOf(this.state.sides.player)?.instanceId ?? null;

    // Apply EV → EXP → level-ups on cloned instances, collecting the juicy moments so we
    // can present them in team order (the front mon animates its EXP gauge + stat panel).
    const updated: PokemonInstance[] = [];
    const evolutions: { mon: PokemonInstance; from: string; to: SpeciesData }[] = [];
    interface Grown {
      clone: PokemonInstance;
      species: SpeciesData;
      gain: number;
      isActive: boolean;
      preLevel: number;
      preExp: number;
      toLevel: number;
      statGains: Readonly<Record<string, number>>;
      newMoves: string[];
      leveled: boolean;
    }
    const grown: Grown[] = [];

    for (const member of team) {
      const gain = totalAwards.get(member.id) ?? 0;
      const clone = cloneInstance(member);
      const species = this.speciesOf(clone.speciesId);
      if (gain > 0 && species) {
        const participated = this.participants.has(member.id);
        // EVs land BEFORE the level-up recompute so a level's stat panel reflects them.
        if (opts.awardEv && participated && anyEv) {
          clone.evs = awardEvs(clone.evs, evGain);
        }
        const nature = safeNature(clone.natureId);
        const preLevel = clone.level;
        const preExp = clone.exp;
        const events = makeLevelUpEvents(clone, clone.exp + gain, species, nature);
        const lu = events[0];

        // Friendship (quiet real mechanic): a little for taking part in the win, a little
        // more for each level gained. Capped at 255.
        if (opts.winBonus && participated) {
          clone.friendship = friendshipAfter(clone.friendship, FRIENDSHIP_ON_WIN);
        }
        if (lu) {
          clone.friendship = friendshipAfter(clone.friendship, FRIENDSHIP_ON_LEVEL_UP);
          this.learnNewMoves(clone, lu.newMoves);
          const match = checkEvolution(clone, species, 'level-up');
          if (match) {
            const toSpecies = this.speciesOf(match.toSpeciesId);
            if (toSpecies) evolutions.push({ mon: clone, from: species.id, to: toSpecies });
          }
        }

        grown.push({
          clone,
          species,
          gain,
          isActive: clone.id === activeId,
          preLevel,
          preExp,
          toLevel: clone.level,
          statGains: lu?.statGains ?? {},
          newMoves: lu ? [...lu.newMoves] : [],
          leveled: !!lu,
        });
      }
      updated.push(clone);
    }

    // Present the gains in team order. The FRONT mon animates its EXP gauge (wrapping on
    // each level) + shows a compact classic stat panel; bench mons still get their warm
    // grew-to / learned-move lines. Level-ups always linger, even in TURBO.
    for (const g of grown) {
      const name = this.monName(g.clone);
      await this.message(`${name} gained ${g.gain} EXP. Points!`);
      if (g.isActive) {
        await this.animatePlayerExpBar(g.species, g.preLevel, g.preExp, g.clone.level, g.clone.exp);
      }
      if (g.leveled) {
        await this.lingerMessage(`${name} grew to Lv ${g.toLevel}!`);
        if (g.isActive) {
          await this.showStatPanel(name, g.statGains);
        }
        for (const mv of g.newMoves) {
          const move = this.moveOf(mv);
          await this.lingerMessage(`${name} learned ${move?.name ?? mv}!`);
        }
      }
    }

    // Evolutions (apply + linger). Evolving updates the instance in `updated` in place.
    // Each one offers a calm CANCEL choice — the level-up always stands; only the
    // species change is opt-in. A cancelled mon simply keeps its current form.
    for (const evo of evolutions) {
      await this.playEvolution(evo.mon, evo.to, updated);
    }

    // Fold the post-battle team back into the world.
    await this.session.applyBattleResult({
      updatedTeam: updated,
      seenSpeciesIds: [...this.seen],
      caught: this.caughtMon,
    });
  }

  /**
   * A glowing evolution flourish; replaces the mon in `updated` with its evolved form —
   * UNLESS the player chooses to stop it. After "What? <Name> is evolving!" we present a
   * calm, no-pressure choice (Evolve / Keep as is): tap a row, or use the keyboard
   * (Enter/Space = evolve, Esc or B = cancel). On EVOLVE the existing flourish + swap run;
   * on CANCEL the mon keeps its current species (the level-up the pipeline already applied
   * still stands) and we show "<Name> stopped evolving." The rest of the pipeline (other
   * mons) continues normally either way.
   */
  private async playEvolution(
    mon: PokemonInstance,
    toSpecies: SpeciesData,
    updated: PokemonInstance[],
  ): Promise<void> {
    const fromName = this.monName(mon);
    await this.lingerMessage(evolvingPromptMessage(fromName));

    // The calm cancel point: let the player choose to evolve or keep the current form.
    const evolve = await this.askEvolutionChoice(toSpecies.name);
    if (!evolve) {
      await this.lingerMessage(stoppedEvolvingMessage(fromName));
      return;
    }

    // Show it on the player platform with a gentle white pulse if it's the active mon.
    const active = activeOf(this.state.sides.player);
    const isActive = active?.instanceId === mon.id;
    if (isActive && !this.reduced) {
      await this.evolutionPulse();
    }

    const evolved = evolveInstance(mon, toSpecies);
    const idx = updated.findIndex((m) => m.id === mon.id);
    if (idx >= 0) updated[idx] = evolved;

    if (isActive) {
      // Repaint the on-field sprite to the evolved species.
      this.playerView.shownDex = -1;
      // Temporarily reflect the evolved species on the engine instance for the sprite.
      (active!.instance as { speciesId: string }).speciesId = toSpecies.id;
      await this.loadSprite('player');
    }
    await this.lingerMessage(evolvedMessage(fromName, toSpecies.name));
  }

  /**
   * The no-pressure CANCEL gate at the evolving moment. Shows a small classic selection
   * box anchored above the message box — "Evolve" / "Keep as is" — usable by TOUCH (tap a
   * row) and KEYBOARD (↑/↓ to move, Enter/Space to confirm, Esc OR B to cancel/keep).
   * Resolves true to evolve, false to keep the current form. Teardown-safe: if the scene
   * shuts down while the choice is open, it resolves false (keep) and removes its key
   * listener so nothing lingers.
   */
  private askEvolutionChoice(toName: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.hideMenu();
      // Keep the "is evolving!" line on screen behind the choice box.
      this.dialog.setVisible(true);

      let settled = false;
      // The classic "B" cancel the owner asked for (Esc is handled by the Menu's onCancel).
      const bKeyHandler = (e: KeyboardEvent): void => {
        if (isEvolutionCancelKey(e.key) && e.key !== 'Escape') {
          e.preventDefault();
          finish(false);
        }
      };
      const finish = (evolve: boolean): void => {
        if (settled) return;
        settled = true;
        if (typeof window !== 'undefined') {
          window.removeEventListener('keydown', bKeyHandler);
        }
        this.hideMenu();
        resolve(evolve);
      };

      // If the scene tears down mid-choice, default to KEEP (never strand a promise).
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => finish(false));

      const labels = evolutionChoiceLabels(toName);
      this.showMenu(
        [
          { label: labels.evolve, onSelect: () => finish(true) },
          { label: labels.keep, onSelect: () => finish(false) },
        ],
        { onCancel: () => finish(false) },
      );
      if (typeof window !== 'undefined') {
        window.addEventListener('keydown', bKeyHandler);
      }
    });
  }

  private evolutionPulse(): Promise<void> {
    const sprite = this.playerView.sprite;
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        alpha: 0.2,
        yoyo: true,
        repeat: 3,
        duration: 160,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          sprite.setAlpha(1);
          resolve();
        },
      });
    });
  }

  /**
   * Learn new moves — UNLIMITED MOVES: every learned move is simply ADDED (full PP,
   * uses:0), deduped against what the mon already knows. No 4-move cap, no replace
   * prompt, nothing is ever forgotten (CLAUDE.md pillar 6: playful, never "wasted").
   */
  private learnNewMoves(mon: PokemonInstance, moveIds: readonly string[]): void {
    for (const id of moveIds) {
      if (mon.moves.some((m) => m.moveId === id)) continue;
      const move = this.moveOf(id);
      if (!move) continue;
      mon.moves.push({ moveId: id, pp: move.pp, maxPp: move.pp, uses: 0 });
    }
  }

  private async endBattleMessage(winner: BattleSideId | 'draw'): Promise<void> {
    // A catch is concluded entirely by runCatchConclusion (Gotcha/dex/nickname/EXP/box)
    // before endBattleMessage is reached, so there is no caught branch here.
    if (this.params.kind === 'friendly') {
      await this.lingerMessage(
        winner === 'player'
          ? 'A lovely match — well played!'
          : 'A lovely match — let’s spar again!',
      );
      await this.persist(); // friendlies still update the dex with what we saw
      this.endBattle();
      return;
    }
    if (winner === 'player') {
      // The leveling pipeline already persisted the team + seen species.
      await this.lingerMessage(`${this.oppLabel()} was defeated!`);
      this.endBattle();
      return;
    }
    if (winner === 'opponent') {
      // NO-STAKES loss — warm, no penalty.
      await this.lingerMessage('Your team is worn out. Let’s head home and rest.');
      await this.persist();
      this.endBattle();
      return;
    }
    // Draw (a run, or a flee).
    await this.lingerMessage(this.params.kind === 'wild' ? 'Got away safely.' : 'Until next time!');
    await this.persist();
    this.endBattle();
  }

  /**
   * Fold the outcome into the world. The EXP-win path persists via the leveling
   * pipeline; every other terminal path (catch / loss / flee / friendly) routes
   * through here so the dex always records what the player saw, exactly once.
   */
  private async persist(extra: { caught?: PokemonInstance | null } = {}): Promise<void> {
    await this.session.applyBattleResult({
      seenSpeciesIds: [...this.seen],
      caught: extra.caught ?? null,
    });
  }

  private endBattle(): void {
    const target = this.params.returnScene ?? 'Town';
    // For a Training (tower) wave, tell the Training scene whether the player won so
    // its loop can show a warm beat ("Nicely done!" / "Good effort"). Harmless for
    // every other return target (it just carries an extra ignored field).
    let data: Record<string, unknown> =
      this.params.kind === 'tower'
        ? { ...(this.params.returnData ?? {}), lastWaveWon: this.lastWinner === 'player' }
        : (this.params.returnData ?? {});
    // Gym round-trip: when the caller tagged this battle with a gymId (GymScene), report
    // the outcome back so the anteroom can play the leader's onWin/onLose lines and fire
    // the badge-progress event. A non-win (loss, flee, or bow-out) reads as 'lost' — but
    // gyms are no-stakes, so 'lost' just means "not won"; there is never a penalty.
    if (data['gymId'] !== undefined) {
      data = { ...data, gymResult: this.lastWinner === 'player' ? 'won' : 'lost' };
    }
    goTo(this, target, { data });
  }

  // ── Catch animation (a REAL ball throw, not printed words) ─────────────────────────

  /**
   * The full catch sequence: a Poké Ball arcs from the player's side up to the wild mon,
   * the mon beams into it (white flash + shrink), the ball drops to the ground and rocks
   * once per engine shake (with tension between), then either clicks shut ('Gotcha!' is
   * spoken by runCatchConclusion) or bursts open and the mon pops back out. Reduced-motion
   * keeps every beat but snaps the tweens.
   */
  private async playCatch(shakes: number, caught: boolean): Promise<void> {
    const mon = this.oppView.sprite;
    const origScale = mon.scale;
    await this.message('You threw a Ball…');

    // Ball starts near the player (lower-left) and arcs to the wild mon's body.
    const from = this.playerView.sprite;
    const targetX = mon.x;
    const targetY = mon.y - (mon.displayHeight || 90) * 0.5;
    const ball = this.makeBall(from.x + 20, from.y - 50);

    if (!this.reduced) {
      await this.arcBall(ball, targetX, targetY);
    } else {
      ball.setPosition(targetX, targetY);
    }

    // Beam the mon in: a quick white flash, then shrink into the ball.
    if (!this.reduced) {
      mon.setTintFill(0xffffff);
      await this.tweenPromise(
        mon,
        { scaleX: origScale * 0.15, scaleY: origScale * 0.15, alpha: 0 },
        220,
      );
      mon.clearTint();
    } else {
      mon.setAlpha(0);
    }
    mon.setVisible(false);

    // Ball drops to the ground under the wild mon's feet.
    const groundY = mon.y + 4;
    if (!this.reduced) {
      await this.tweenPromise(ball, { y: groundY, rotation: 0 }, 200);
    } else {
      ball.setPosition(targetX, groundY);
    }

    // One REAL wobble per engine shake, with tension between (never fewer than one).
    const n = Math.max(1, shakes || 1);
    for (let i = 0; i < n; i++) {
      await this.wait(this.reduced ? 40 : 420);
      await this.wobbleBall(ball);
    }

    if (caught) {
      await this.wait(this.reduced ? 40 : 260);
      await this.ballClick(ball);
      // The ball settles; runCatchConclusion says "Gotcha!" and folds the mon in.
    } else {
      await this.wait(this.reduced ? 20 : 160);
      await this.ballBurst(ball);
      ball.destroy();
      // Mon pops back out.
      mon
        .setScale(origScale)
        .setAlpha(this.reduced ? 1 : 0)
        .setVisible(true);
      if (!this.reduced) {
        mon.setScale(origScale * 0.4);
        await this.tweenPromise(mon, { scaleX: origScale, scaleY: origScale, alpha: 1 }, 200);
      }
      await this.message('Oh no — it broke free!');
    }
  }

  /**
   * A throwable Poké Ball: the vendored `pokeball` fx texture when it loaded, else a
   * drawn red/white ball. Returned as a GameObject with rotation/scale/alpha for tweens.
   */
  private makeBall(x: number, y: number): Phaser.GameObjects.Container | Phaser.GameObjects.Image {
    if (this.textures.exists('pokeball')) {
      return this.add.image(x, y, 'pokeball').setScale(1.5).setDepth(900);
    }
    const g = this.add.graphics();
    const rad = 11;
    g.fillStyle(COLORS.hpFrame, 1);
    g.fillCircle(0, 0, rad + 1.5);
    g.fillStyle(COLORS.rose, 1);
    g.slice(0, 0, rad, Math.PI, Math.PI * 2, false);
    g.fillPath();
    g.fillStyle(COLORS.boxFill, 1);
    g.slice(0, 0, rad, 0, Math.PI, false);
    g.fillPath();
    g.fillStyle(COLORS.hpFrame, 1);
    g.fillRect(-rad, -2, rad * 2, 4);
    g.fillStyle(COLORS.boxFill, 1);
    g.fillCircle(0, 0, 3.4);
    g.lineStyle(1.5, COLORS.hpFrame, 1);
    g.strokeCircle(0, 0, 3.4);
    return this.add.container(x, y, [g]).setDepth(900);
  }

  /** Parabolic toss of the ball from its current spot to (x1,y1), tumbling as it flies. */
  private arcBall(
    ball: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
    x1: number,
    y1: number,
  ): Promise<void> {
    const x0 = ball.x;
    const y0 = ball.y;
    const peak = Math.min(y0, y1) - 70;
    return new Promise((resolve) => {
      const proxy = { t: 0 };
      const tween = this.tweens.add({
        targets: proxy,
        t: 1,
        duration: Math.max(120, 440 * this.pace),
        ease: 'Sine.easeInOut',
        onUpdate: () => {
          const t = proxy.t;
          ball.x = x0 + (x1 - x0) * t;
          ball.y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * peak + t * t * y1;
          ball.rotation = t * Math.PI * 2.2;
        },
        onComplete: () => {
          ball.x = x1;
          ball.y = y1;
          resolve();
        },
      });
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        tween.remove();
        resolve();
      });
    });
  }

  /** One left-right rock of the ball on the ground (the classic wobble). */
  private async wobbleBall(
    ball: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
  ): Promise<void> {
    if (this.reduced) return;
    const a = 0.4;
    await this.tweenPromise(ball, { rotation: -a }, 120);
    await this.tweenPromise(ball, { rotation: a }, 170);
    await this.tweenPromise(ball, { rotation: 0 }, 120);
  }

  /** The satisfying click: a small settle-pulse of the caught ball. */
  private async ballClick(
    ball: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
  ): Promise<void> {
    if (this.reduced) return;
    const s = ball.scale;
    await this.tweenPromise(ball, { scaleX: s * 1.25, scaleY: s * 1.25 }, 90);
    await this.tweenPromise(ball, { scaleX: s, scaleY: s }, 110);
  }

  /** The ball bursts open on a break-free (flash-pulse + fade). */
  private async ballBurst(
    ball: Phaser.GameObjects.Container | Phaser.GameObjects.Image,
  ): Promise<void> {
    if (this.reduced) return;
    const s = ball.scale;
    await this.tweenPromise(ball, { scaleX: s * 1.5, scaleY: s * 1.5, alpha: 0 }, 160);
  }

  // ── Level-up stat panel + nickname prompt ──────────────────────────────────────────

  /**
   * A compact classic cream panel showing the per-stat gains of a level-up (+HP / +Atk /
   * …). Dismissed by a tap anywhere or Enter/Space/Esc — a calm, un-nagging payoff. Under
   * reduced-motion it still shows and waits for a dismiss. Teardown-safe.
   */
  private showStatPanel(name: string, statGains: Readonly<Record<string, number>>): Promise<void> {
    return new Promise((resolve) => {
      const rows: { label: string; delta: number }[] = [
        { label: 'HP', delta: statGains.hp ?? 0 },
        { label: 'Attack', delta: statGains.atk ?? 0 },
        { label: 'Defense', delta: statGains.def ?? 0 },
        { label: 'Sp. Atk', delta: statGains.spatk ?? 0 },
        { label: 'Sp. Def', delta: statGains.spdef ?? 0 },
        { label: 'Speed', delta: statGains.spe ?? 0 },
      ];
      const safe = this.layout.safe;
      const w = Math.min(safe.width - SPACING.lg * 2, 236);
      const rowH = 22;
      const headH = 30;
      const footH = 22;
      const h = headH + rows.length * rowH + footH + SPACING.sm;
      const cx = safe.x + safe.width / 2;
      const cy = safe.y + safe.height / 2;
      const box = this.add.container(cx, cy).setDepth(1200);

      const g = this.add.graphics();
      g.fillStyle(COLORS.scrim, 0.4);
      g.fillRoundedRect(-w / 2 + 3, -h / 2 + 4, w, h, RADIUS.md);
      g.fillStyle(COLORS.boxFill, 1);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
      g.lineStyle(3, COLORS.boxBorder, 1);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
      g.lineStyle(1, COLORS.boxBorderInner, 1);
      g.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, Math.max(2, RADIUS.md - 4));
      box.add(g);

      const title = this.add
        .text(0, -h / 2 + SPACING.sm, `${name} grew stronger!`, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.boxText,
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0);
      box.add(title);

      rows.forEach((r, i) => {
        const y = -h / 2 + headH + i * rowH;
        const label = this.add
          .text(-w / 2 + SPACING.md, y, r.label, {
            fontFamily: FONT.family,
            fontSize: `${FONT.size.xs}px`,
            color: CSS.boxTextMuted,
          })
          .setOrigin(0, 0);
        const delta = this.add
          .text(w / 2 - SPACING.md, y, `+${r.delta}`, {
            fontFamily: FONT.mono,
            fontSize: `${FONT.size.xs}px`,
            color: r.delta > 0 ? CSS.accent : CSS.boxTextMuted,
            fontStyle: 'bold',
          })
          .setOrigin(1, 0);
        box.add([label, delta]);
      });

      const hint = this.add
        .text(0, h / 2 - SPACING.sm, '▼  tap / Enter', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textFaint,
        })
        .setOrigin(0.5, 1);
      box.add(hint);

      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        if (typeof window !== 'undefined') window.removeEventListener('keydown', onKey);
        this.input.off('pointerdown', onTap);
        box.destroy();
        resolve();
      };
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
          e.preventDefault();
          finish();
        }
      };
      const onTap = (): void => finish();
      // Bind a tick later so the tap/enter that reached the level-up doesn't instantly close it.
      this.time.delayedCall(1, () => {
        if (!settled) this.input.on('pointerdown', onTap);
      });
      if (typeof window !== 'undefined') window.addEventListener('keydown', onKey);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, finish);
    });
  }

  /**
   * A small nickname dialog for a fresh catch. Reuses the touch-first {@link TextInput}
   * (OS keyboard on phones); OK / Enter keeps the typed name, Skip / Esc keeps the species
   * name. Resolves the trimmed nickname, or null to keep the default. Teardown-safe.
   */
  private askNickname(defaultName: string): Promise<string | null> {
    return new Promise((resolve) => {
      this.hideMenu();
      const safe = this.layout.safe;
      const w = Math.min(safe.width - SPACING.lg * 2, 320);
      const h = 176;
      const cx = safe.x + safe.width / 2;
      const cy = safe.y + safe.height / 2;

      const box = this.add.container(cx, cy).setDepth(1200);
      const g = this.add.graphics();
      g.fillStyle(COLORS.scrim, 0.4);
      g.fillRoundedRect(-w / 2 + 3, -h / 2 + 4, w, h, RADIUS.md);
      g.fillStyle(COLORS.boxFill, 1);
      g.fillRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
      g.lineStyle(3, COLORS.boxBorder, 1);
      g.strokeRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
      g.lineStyle(1, COLORS.boxBorderInner, 1);
      g.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, Math.max(2, RADIUS.md - 4));
      const title = this.add
        .text(0, -h / 2 + SPACING.md, `Give ${defaultName} a nickname?`, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.boxText,
          fontStyle: 'bold',
          wordWrap: { width: w - SPACING.lg },
          align: 'center',
        })
        .setOrigin(0.5, 0);
      box.add([g, title]);

      // Interactive widgets sit at absolute coords above the panel (they auto-add to the
      // scene root, so nesting them in the container would reparent awkwardly).
      const input = new TextInput(this, cx, cy - 6, {
        width: w - SPACING.lg,
        placeholder: defaultName,
        maxLength: 16,
        onSubmit: (v) => finish(v),
      });
      input.setDepth(1201);
      const btnW = (w - SPACING.md * 3) / 2;
      const okBtn = new Button(this, cx - btnW / 2 - SPACING.xs, cy + h / 2 - 34, {
        label: 'OK',
        variant: 'primary',
        width: btnW,
        height: 40,
        fontSize: FONT.size.sm,
        reducedMotion: this.reduced,
        onClick: () => finish(input.getValue()),
      });
      okBtn.setDepth(1201);
      const skipBtn = new Button(this, cx + btnW / 2 + SPACING.xs, cy + h / 2 - 34, {
        label: 'Skip',
        variant: 'ghost',
        width: btnW,
        height: 40,
        fontSize: FONT.size.sm,
        reducedMotion: this.reduced,
        onClick: () => finish(null),
      });
      skipBtn.setDepth(1201);

      let settled = false;
      const finish = (val: string | null): void => {
        if (settled) return;
        settled = true;
        if (typeof window !== 'undefined') window.removeEventListener('keydown', onKey);
        input.blur();
        box.destroy();
        input.destroy();
        okBtn.destroy();
        skipBtn.destroy();
        const trimmed = (val ?? '').trim();
        resolve(trimmed.length > 0 ? trimmed : null);
      };
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          e.preventDefault();
          finish(null);
        }
      };
      if (typeof window !== 'undefined') window.addEventListener('keydown', onKey);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => finish(null));
    });
  }

  // ── Tiny animation helpers ─────────────────────────────────────────────────────

  private async lunge(side: BattleSideId): Promise<void> {
    if (this.reduced) return;
    const sprite = this.viewFor(side).sprite;
    const dx = side === 'player' ? 18 : -18;
    const x0 = sprite.x;
    await this.tweenPromise(sprite, { x: x0 + dx }, 90 * this.pace);
    await this.tweenPromise(sprite, { x: x0 }, 90 * this.pace);
  }

  /**
   * Resolve + play the move VFX. This is a RESOLVER over two backends:
   *
   *   1. The ported **Showdown** animation (faithful, art-driven) — used when the move
   *      resolves to a {@link getShowdownAnim} script, the fx textures loaded in Preload
   *      (`this.fxReady`), AND we're not in reduced-motion. It runs `playShowdownAnim`
   *      with two {@link Actor}s built from the on-field sprite anchors + per-side facing.
   *   2. The **procedural** {@link playMoveEffect} (type/category-coloured beam/lunge/aura)
   *      — the unchanged fallback for every other case: no Showdown script for the move,
   *      the fx pack didn't load, OR reduced-motion (which always takes the gentle
   *      procedural path).
   *
   * Either backend resolves only after every spawned object is destroyed, so the caller
   * can safely drain HP next (the effect is awaited BEFORE the following 'damage' event).
   * Both are self-cleaning + teardown-safe, so this stays re-entry safe; never throws.
   */
  private async playMoveVfx(
    side: BattleSideId,
    move: MoveData | undefined,
    moveId: string,
  ): Promise<void> {
    if (!this.scene.isActive() || !move) return;
    const target: BattleSideId = side === 'player' ? 'opponent' : 'player';

    // Tier 1 — the ported Showdown choreography (only with art loaded + full motion).
    if (this.fxReady && !this.reduced) {
      const anim = getShowdownAnim(moveId, move.category);
      if (anim) {
        const attacker = this.makeActor(side);
        const defender = this.makeActor(target);
        await playShowdownAnim(this, anim, [attacker, defender], {
          pace: this.pace,
          reduced: this.reduced,
        });
        return;
      }
    }

    // Tier 2 — the procedural fallback (unchanged). Reduced-motion always lands here.
    await playMoveEffect(this, {
      moveId,
      moveName: move.name,
      type: move.type,
      category: move.category,
      from: this.effectAnchor(side),
      to: this.effectAnchor(target),
      reduced: this.reduced,
      pace: this.pace,
    });
  }

  /**
   * Build a Showdown {@link Actor} for a side: its live combatant sprite (so the ported
   * scripts can lunge it) + the logical x/y/z anchor + the per-side facing flag.
   *
   * Coordinate mapping. Showdown describes effects in a logical field that
   * {@link playShowdownAnim} maps to the screen via a {@link FieldTransform} FITTED to the
   * two actors' DISTINCT logical anchors: the near (player) actor carries logical z={@link
   * NEAR_Z} and the far (opponent) actor z={@link FAR_Z}, and the fit maps each onto its
   * real sprite anchor. That is the fix for the "beam collapses to a point" bug — with both
   * actors previously parked at z=0 over a shared midpoint origin, any script's `attacker ->
   * defender` span had zero length; now the span travels the full field so beams stretch
   * from caster to target.
   *
   * We pass each Actor a tiny TRANSFORM PROXY whose reported (x, y) is the sprite's BODY
   * CENTRE (the {@link effectAnchor}), so the fit anchors the field on the bodies and effects
   * land on bodies — and whose y-SETTER writes back through to the real (feet-anchored,
   * origin 0.5/1) sprite, so when a script's `actor.anim(...)` lunges the actor it visually
   * moves the real mon correctly. Each actor's logical (x=0, y=0, z=NEAR/FAR) resolves back
   * to its own body-centre anchor, so `anim({})` is a clean no-op resting pose. `isFrontSprite`
   * = the opponent (far) side, which flips PS's facing signs (behind/leftof) so the
   * choreography reads correctly for whichever way the mon faces.
   */
  private makeActor(side: BattleSideId): Actor {
    const sprite = this.viewFor(side).sprite;
    const anchor = this.effectAnchor(side);
    // Feet→body delta: effectAnchor lifts to the body centre; the real sprite's y is its
    // feet. The proxy reports the body y and, on write, restores the feet offset so the
    // bottom-anchored sprite ends with its BODY where a lunge tween targets.
    const bodyOffset = sprite.y - anchor.y;
    const proxy: Phaser.GameObjects.Components.Transform = {
      get x() {
        return sprite.x;
      },
      set x(v: number) {
        sprite.x = v;
      },
      get y() {
        return sprite.y - bodyOffset;
      },
      set y(v: number) {
        sprite.y = v + bodyOffset;
      },
      // Guard read by the engine's ballistic onUpdate; mirror the real sprite's liveness.
      get active() {
        return sprite.active;
      },
    } as unknown as Phaser.GameObjects.Components.Transform;

    const isFront = side === 'opponent';
    return new Actor(proxy, {
      x: 0,
      y: 0,
      z: isFront ? FAR_Z : NEAR_Z,
      isFrontSprite: isFront,
    });
  }

  /**
   * The world-space anchor a move effect aims at for a side: the visual centre of that
   * side's combatant sprite. The sprite has origin (0.5, 1) seated at `pos.y + 34`
   * (its feet), so we lift the anchor by ~half the scaled sprite height to land on the
   * body rather than the ground. Player = the back sprite (lower-left); opponent = the
   * front sprite (upper-right).
   */
  private effectAnchor(side: BattleSideId): { x: number; y: number } {
    const sprite = this.viewFor(side).sprite;
    const h = sprite.displayHeight || 96;
    return { x: sprite.x, y: sprite.y - h * 0.5 };
  }

  private async flashHit(side: BattleSideId): Promise<void> {
    if (this.reduced) return;
    const sprite = this.viewFor(side).sprite;
    await this.tweenPromise(sprite, { alpha: 0.25 }, 80 * this.pace);
    await this.tweenPromise(sprite, { alpha: 1 }, 80 * this.pace);
  }

  private async faintDrop(side: BattleSideId): Promise<void> {
    const view = this.viewFor(side);
    if (this.reduced) {
      view.sprite.setVisible(false);
      return;
    }
    const sprite = view.sprite;
    await this.tweenPromise(sprite, { y: sprite.y + 26, alpha: 0 }, 280 * this.pace);
    sprite.setVisible(false);
  }

  private tweenPromise(
    target: Phaser.GameObjects.GameObject,
    props: Record<string, number>,
    duration: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({
        targets: target,
        ...props,
        duration: duration <= 0 ? 1 : duration,
        ease: 'Sine.easeInOut',
        onComplete: () => resolve(),
      });
    });
  }

  /** Show a battle message and wait for the dwell (pace-scaled). */
  private message(text: string): Promise<void> {
    return new Promise((resolve) => {
      this.dialog.setVisible(true);
      this.dialog.show(text);
      const dwell = MESSAGE_DWELL * this.pace;
      const timer = this.time.delayedCall(Math.max(60, dwell), resolve);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => timer.remove(false));
    });
  }

  /** Like {@link message} but always lingers (level-up / evolution flourishes). */
  private lingerMessage(text: string): Promise<void> {
    return new Promise((resolve) => {
      this.dialog.setVisible(true);
      this.dialog.show(text);
      const dwell = this.reduced ? 700 : 1300;
      const timer = this.time.delayedCall(dwell, resolve);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => timer.remove(false));
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = this.time.delayedCall(Math.max(1, ms * this.pace), resolve);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => timer.remove(false));
    });
  }

  // ── Turbo toggle ───────────────────────────────────────────────────────────────

  private buildTurboToggle(): void {
    this.turboButton = new Button(this, 0, 0, {
      label: '» Fast',
      variant: 'ghost',
      width: 96,
      height: 40,
      fontSize: FONT.size.xs,
      reducedMotion: this.reduced,
      onClick: () => this.toggleTurbo(),
    });
  }

  private toggleTurbo(): void {
    this.pace = this.pace === PACE_NORMAL ? PACE_TURBO : PACE_NORMAL;
    this.turboButton?.setLabel(this.pace === PACE_NORMAL ? '» Fast' : '» Calm');
  }

  // ── Layout (responsive phone ↔ desktop) ─────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    this.drawField();

    // Cards are CARD_W wide (centered origin); clamp each so its full width stays inside
    // the safe rect — never clipped off an edge, even with a notch/home-indicator inset.
    const cardHalfW = CARD_W / 2;
    const cardHalfH = CARD_H / 2;
    const clampCardX = (x: number): number =>
      Math.min(
        safe.x + safe.width - cardHalfW - SPACING.sm,
        Math.max(safe.x + cardHalfW + SPACING.sm, x),
      );

    // Opponent card top-LEFT; player card on the RIGHT.
    const oppCardX = clampCardX(safe.x + cardHalfW + SPACING.md);
    const oppCardY = safe.y + cardHalfH + SPACING.lg;
    this.oppView.panel.setPosition(oppCardX, oppCardY);

    const plCardX = clampCardX(safe.x + safe.width - cardHalfW - SPACING.md);
    const dialogTop = safe.y + safe.height - this.dialogHeight() - SPACING.md;
    // On a roomy desktop the centered command menu never reaches the far-right card, so
    // the player nameplate sits just above the message box (the classic spot). On a
    // NARROW phone that same band is where the FIGHT/Bag menu opens (centered + wide), so
    // we LIFT the player nameplate to sit clear ABOVE the command-menu band — fully on
    // screen and never overlapping the menu.
    const plCardY = this.layout.isCompact
      ? this.commandBandTop() - SPACING.sm - cardHalfH
      : dialogTop - 50;
    this.playerView.panel.setPosition(plCardX, plCardY);

    // Combatant sprites.
    this.positionSprite('opponent');
    this.positionSprite('player');

    // Dialog box: full-width-ish along the bottom.
    const dWidth = Math.min(safe.width - SPACING.md * 2, 560);
    const dHeight = this.dialogHeight();
    this.dialog.setPosition(
      safe.x + safe.width / 2,
      safe.y + safe.height - dHeight / 2 - SPACING.sm,
    );
    this.resizeDialog(dWidth, dHeight);

    // Turbo toggle top-right.
    this.turboButton?.setPosition(safe.x + safe.width - 56 - SPACING.sm, safe.y + 28);

    // Re-anchor any open selection box above the message box.
    this.positionMenu();
  }

  private combatantPos(side: BattleSideId): { x: number; y: number } {
    const safe = this.layout.safe;
    if (side === 'opponent') {
      return { x: safe.x + safe.width * 0.72, y: safe.y + safe.height * 0.4 };
    }
    // On a narrow phone, seat the player back-sprite further into the lower-LEFT (0.20 vs
    // the roomy 0.28) so its right edge stays clear of the right-pinned info card — the
    // full nameplate then reads (see loadSprite for the paired size trim). The classic
    // lower-left back-sprite / lower-right card diagonal is preserved.
    const playerXFactor = this.layout.isCompact ? 0.2 : 0.28;
    return { x: safe.x + safe.width * playerXFactor, y: safe.y + safe.height * 0.66 };
  }

  private dialogHeight(): number {
    return this.layout.isCompact ? 92 : 104;
  }

  /**
   * The top y of the bottom command-menu band — where the FIGHT/Bag/move selection box
   * opens (anchored just above the message box). Used on a phone to seat the player
   * nameplate clear ABOVE this band so the two never overlap. Mirrors positionMenu()'s
   * anchoring, sized to the TALLEST 2-column box in the normal turn flow: the move menu
   * (up to 4 moves + Back = 3 rows), which also covers the 2-row main FIGHT/Bag menu.
   * (The rarer tall 1-column switch/bag lists clamp to the safe top and scroll instead.)
   */
  private commandBandTop(): number {
    const safe = this.layout.safe;
    const anchorY = safe.y + safe.height - this.dialogHeight() - SPACING.md;
    // 3 rows of TOUCH_TARGET-tall cells + the box's vertical padding.
    const commandMenuH = 3 * TOUCH_TARGET + SPACING.sm * 2;
    return anchorY - commandMenuH - SPACING.sm;
  }

  private resizeDialog(width: number, height: number): void {
    // DialogBox has no public resize; rebuild it if the width changed materially.
    const cur = this.dialog as unknown as { bWidth: number };
    if (Math.abs((cur.bWidth ?? width) - width) < 2) return;
    const visible = this.dialog.visible;
    this.dialog.destroy();
    this.dialog = new DialogBox(this, 0, 0, { width, height, reducedMotion: this.reduced });
    this.dialog.setVisible(visible);
    const safe = this.layout.safe;
    this.dialog.setPosition(
      safe.x + safe.width / 2,
      safe.y + safe.height - height / 2 - SPACING.sm,
    );
  }

  // Menu placement ------------------------------------------------------------------

  /**
   * Anchor the open selection box centered just above the bottom message box. The
   * Menu sizes itself (boxWidth/boxHeight), so we only place it within the safe area.
   */
  private positionMenu(): void {
    const menu = this.menu;
    if (!menu) return;
    const safe = this.layout.safe;
    // `anchorY` is the TOP of the bottom message box: the selection box must sit ABOVE it
    // so the two never overlap. Seat the menu's bottom just above anchorY; if the menu is
    // taller than the band above the message box (a tall switch list on a SHORT/landscape
    // phone), clamp its top to the safe top so it extends up over the (empty) field rather
    // than ever covering the message box. On a normal portrait phone it simply nestles in.
    const anchorY = safe.y + safe.height - this.dialogHeight() - SPACING.md;
    const cx = safe.x + safe.width / 2;
    const half = menu.boxHeight / 2;
    const top = Math.max(safe.y + SPACING.sm, anchorY - menu.boxHeight - SPACING.sm);
    menu.setPosition(cx, top + half);
  }

  // ── Small data/name helpers ───────────────────────────────────────────────────────

  private viewFor(side: BattleSideId): SideView {
    return side === 'player' ? this.playerView : this.oppView;
  }

  private speciesOf(id: string): SpeciesData | undefined {
    return this.index.speciesById(id);
  }

  private moveOf(id: string): MoveData | undefined {
    return this.index.moveById(id);
  }

  private displayName(p: BattleParticipant): string {
    const species = this.speciesOf(p.instance.speciesId);
    return p.instance.nickname ?? species?.name ?? p.instance.speciesId;
  }

  private monName(mon: PokemonInstance): string {
    return mon.nickname ?? this.speciesOf(mon.speciesId)?.name ?? mon.speciesId;
  }

  private activeName(side: BattleSideId): string {
    const p = activeOf(this.state.sides[side]);
    return p ? this.displayName(p) : '—';
  }

  private instanceNameById(side: BattleSideId, instanceId: string): string {
    const p = this.state.sides[side].party.find((x) => x.instanceId === instanceId);
    return p ? this.displayName(p) : '—';
  }

  private oppLabel(): string {
    return this.params.kind === 'wild'
      ? `the wild ${this.activeName('opponent')}`
      : this.state.sides.opponent.trainerName;
  }

  /** A fixtures-backed index when the registry has none (defensive parity w/ NewGame). */
  private sessionIndex(): DataIndex {
    return this.session.bundle.index;
  }

  /** A minimal fallback mon (never expected — the team/opponent should be non-empty). */
  private fallbackMon(): PokemonInstance {
    const species = this.session.bundle.species[0]!;
    const nature = safeNature('hardy');
    const stats = computeStats(species.baseStats, zeroIv(), zeroEv(), 5, nature);
    const move = species.learnset.find((l) => l.method === 'level-up');
    const moveId = move?.moveId ?? 'tackle';
    const m = this.moveOf(moveId);
    return {
      id: `fallback-${species.id}`,
      speciesId: species.id,
      nickname: null,
      level: 5,
      exp: 0,
      natureId: 'hardy',
      ivs: zeroIv(),
      evs: zeroEv(),
      abilityId: species.abilities.primary,
      moves: [{ moveId, pp: m?.pp ?? 35, maxPp: m?.pp ?? 35 }],
      currentHp: stats.hp,
      maxHp: stats.hp,
      status: 'none',
      statusTurns: 0,
      heldItemId: null,
      friendship: 70,
      shiny: false,
      gender: null,
      originalTrainer: {
        trainerId: 'player',
        trainerName: this.session.trainerName,
        viaTrade: false,
      },
      obtainedAt: Date.now(),
    };
  }
}

// ── Pure helpers (no scene state) ──────────────────────────────────────────────────

function activeOf(side: BattleSide): BattleParticipant | undefined {
  return side.party[side.activeIndex];
}

function firstUsableIndex(party: readonly PokemonInstance[]): number {
  const idx = party.findIndex((p) => p.currentHp > 0);
  return idx >= 0 ? idx : 0;
}

function toParticipant(inst: PokemonInstance): BattleParticipant {
  return {
    instanceId: inst.id,
    instance: cloneInstance(inst),
    statStages: {},
    volatile: {},
  };
}

function cloneInstance(m: PokemonInstance): PokemonInstance {
  return {
    ...m,
    ivs: { ...m.ivs },
    evs: { ...m.evs },
    moves: m.moves.map((mv) => ({ ...mv })),
    originalTrainer: { ...m.originalTrainer },
  };
}

/** A caught wild mon, re-stamped as the player's own (origin = player). */
function freshenCaught(inst: PokemonInstance, playerName: string): PokemonInstance {
  return {
    ...cloneInstance(inst),
    originalTrainer: { trainerId: 'player', trainerName: playerName, viaTrade: false },
    obtainedAt: Date.now(),
  };
}

/** A move slot's lifetime use count (absent === 0) — the move-menu sort key. */
function moveUses(slot: { uses?: number }): number {
  return slot.uses ?? 0;
}

function safeNature(id: string): ReturnType<typeof getNature> {
  try {
    return getNature(id);
  } catch {
    return getNature('hardy');
  }
}

function zeroIv(): PokemonInstance['ivs'] {
  return { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
}
function zeroEv(): PokemonInstance['evs'] {
  return { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
}

/** A short effectiveness cue for a move row vs the current foe (shown in the hint). */
function effectivenessLabel(eff: number): { text: string } {
  if (eff === 0) return { text: 'no effect' };
  if (eff > 1) return { text: 'super!' };
  if (eff < 1) return { text: 'resisted' };
  return { text: '' };
}

/** The classic spoken note after a damaging hit. */
function effectivenessNote(eff: number): string | null {
  if (eff === 0) return 'It had no effect…';
  if (eff >= 2) return "It's super effective!";
  if (eff > 0 && eff < 1) return "It's not very effective…";
  return null;
}

function statusVerb(status: StatusCondition): string {
  switch (status) {
    case 'paralysis':
      return 'paralyzed';
    case 'burn':
      return 'burned';
    case 'poison':
      return 'poisoned';
    case 'toxic':
      return 'badly poisoned';
    case 'sleep':
      return 'put to sleep';
    case 'freeze':
      return 'frozen solid';
    default:
      return 'affected';
  }
}

function statName(stat: string): string {
  const names: Record<string, string> = {
    atk: 'Attack',
    def: 'Defense',
    spatk: 'Sp. Atk',
    spdef: 'Sp. Def',
    spe: 'Speed',
    acc: 'accuracy',
    eva: 'evasiveness',
  };
  return names[stat] ?? stat;
}
